"use client";

import { Select, Text, View } from "@/components";
import { useTheme } from "@/providers";
import type { ApexOptions } from "apexcharts";
import dynamic from "next/dynamic";
import { useState } from "react";

// Lazy load the chart to avoid SSR issues
const ReactApexChart = dynamic(() => import("react-apexcharts"), {
  ssr: false
});

// Default options for the filter dropdown
const DEFAULT_FILTER_OPTIONS = ["Last 7 Days", "Last 15 Days", "Last 1 Month"];

export interface PolarProps {
  /** The data series to display in the chart */
  series: number[];
  /** The labels corresponding to each data point */
  labels: string[];
  /** The colors for each data series */
  colors: string[];
  /** Optional ApexCharts configuration to merge with defaults */
  options?: ApexOptions;
  /** Options for the sort/filter dropdown */
  filterOptions?: string[];
}

/**
 * @component
 * @name Polar
 * @description A customizable Polar Area component that displays data series in a polar coordinate system.
 * It includes a dropdown for filtering or sorting data (visual representation only in this demo).
 * The chart adapts to light and dark modes automatically using Tailwind CSS and standard theme detection.
 *
 * @example
 * ```tsx
 * <Polar
        series={[14, 23, 21, 17, 15, 10, 12, 17, 21]}
        labels={['Application', 'Widgets', 'Input Forms', 'Components', 'Pages', 'Earning List', 'Total Sale', 'Revenue', 'Ads Spent']}
        colors={['#5D69F4', '#00998B', '#FFC861', '#FF6161', '#8169D3']}
        filterOptions={["Week", "Month", "Year"]}
      />
 * ```
 *
 * @param {PolarProps} props - The component properties
 * @param {number[]} props.series - The numerical data to plot
 * @param {string[]} props.labels - Labels for the data categories
 * @param {string[]} props.colors - Array of hex color strings for the series
 * @param {ApexOptions} [props.options] - Additional ApexCharts options
 * @param {string[]} [props.filterOptions] - Items to display in the header dropdown
 * @returns {JSX.Element} The rendered Polar Chart component
 */
const Polar = ({
  series,
  labels,
  colors,
  options,
  filterOptions = DEFAULT_FILTER_OPTIONS
}: PolarProps) => {
  const [selectedFilter, setSelectedFilter] = useState(filterOptions[0]);
  const { theme } = useTheme();
  const isDark = theme === "dark";

  const defaultOptions: ApexOptions = {
    chart: {
      type: "polarArea",
      background: "transparent",
      animations: { enabled: true },
      fontFamily: "inherit",
      toolbar: { show: false }
    },
    labels: labels,
    colors: colors,
    fill: { opacity: 1 },
    stroke: {
      // Borders between series
      width: 1,
      colors: [isDark ? "#1e293b" : "#e2e8f0"]
    },
    yaxis: { show: false },
    legend: {
      // List of items in text
      show: true,
      position: "right",
      fontSize: "13px",
      fontFamily: "inherit",
      fontWeight: 400,
      labels: { colors: isDark ? "#cbd5e1" : "#475569" },
      markers: {
        // Colored circles for items
        size: 6,
        offsetX: -4
      },
      itemMargin: { vertical: 8, horizontal: 10 },
      onItemClick: { toggleDataSeries: true },
      onItemHover: { highlightDataSeries: true }
    },
    plotOptions: {
      // Visual options for the polar area
      polarArea: {
        rings: { strokeWidth: 0 }, // Hide internal rings
        spokes: {
          strokeWidth: 1,
          connectorColors: isDark ? "#334155" : "#e2e8f0"
        } // Lines between series
      }
    },
    theme: {
      mode: isDark ? "dark" : "light",
      palette: "palette1"
    },
    dataLabels: {
      // Text on the chart
      enabled: true,
      style: {
        fontSize: "11px",
        fontWeight: 600,
        colors: ["#1e293b"]
      },
      dropShadow: { enabled: false }
    },
    tooltip: {
      // Text on hover
      theme: isDark ? "dark" : "light",
      fillSeriesColor: false,
      y: {
        formatter: (val: number) => `${val}%`
      }
    },
    grid: {
      padding: { top: 0, bottom: 0, left: 0, right: 0 }
    },
    responsive: [
      {
        breakpoint: 480,
        options: {
          legend: {
            position: "bottom"
          }
        }
      }
    ]
  };

  return (
    <View className="w-full col-span-12 lg:col-span-6 p-6 rounded-2xl bg-white dark:bg-slate-950 border border-slate-200 dark:border-slate-800 shadow-sm">
      <View className="flex flex-wrap items-center justify-between pb-6 mb-2 border-b border-dashed border-slate-200 dark:border-slate-700">
        <Text className="text-base font-semibold text-slate-900 dark:text-white">
          Showcase Info
        </Text>
        <View className="flex items-center gap-3">
          <Text className="text-sm text-slate-500 dark:text-slate-400">
            Sort By :
          </Text>
          <View className="w-32">
            <Select
              items={[]}
              selected={selectedFilter}
              setSelected={setSelectedFilter}
              className="w-full"
            />
          </View>
        </View>
      </View>
      <View
        className="flex items-center justify-center min-h-[350px]"
        aria-label="Polar area chart showing showcase info distribution"
      >
        <View className="w-full h-full max-w-lg">
          <ReactApexChart
            options={
              options ? { ...defaultOptions, ...options } : defaultOptions
            }
            series={series}
            type="polarArea"
            height={380}
            width="100%"
          />
        </View>
      </View>
    </View>
  );
};

export default Polar;
